import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  resolveInvite,
  requestOtp,
  verifyOtp,
  resendOtp,
  INVITE_SESSION_KEYS,
} from './aiInterviewInviteAPI'
import { identifyPostHogUser } from '../../../../utils/posthog'
import {
  trackInviteLinkOpened,
  trackInviteLinkInvalid,
  trackInviteOtpRequested,
  trackInviteOtpRequestFailed,
  trackInviteOtpVerified,
  trackInviteOtpVerificationFailed,
} from '../../../../utils/assessmentEvents'

const COBALT = '#494FDF'
const COBALT_LIGHT = '#F4F5FF'
const COBALT_BORDER = '#E0E2FF'
const TEXT_PRIMARY = '#0f172a'
const TEXT_SECONDARY = '#64748b'
const BORDER = '#e2e8f0'
const ERROR = '#dc2626'
const RADIUS = 6

const shell = {
  minHeight: '100vh',
  background: '#F8FAFC',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px 16px',
  fontFamily: "'Satoshi','Inter','Helvetica Neue',Arial,sans-serif",
  color: TEXT_PRIMARY,
}

const card = {
  width: '100%',
  maxWidth: 440,
  background: '#ffffff',
  border: `1px solid ${BORDER}`,
  borderRadius: RADIUS,
  padding: '36px 32px',
  boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
}

const heading = {
  margin: 0,
  fontSize: 22,
  fontWeight: 700,
  letterSpacing: '-0.01em',
}

const subheading = {
  margin: '6px 0 0',
  fontSize: 14,
  color: TEXT_SECONDARY,
}

const label = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: TEXT_PRIMARY,
  marginBottom: 8,
}

const inputBase = {
  width: '100%',
  height: 44,
  padding: '0 14px',
  border: `1px solid ${BORDER}`,
  borderRadius: RADIUS,
  fontSize: 15,
  outline: 'none',
  background: '#ffffff',
  color: TEXT_PRIMARY,
  transition: 'border-color 120ms ease',
  fontFamily: 'inherit',
}

const buttonPrimary = (disabled) => ({
  width: '100%',
  height: 44,
  background: disabled ? '#A4A8F0' : COBALT,
  color: '#fff',
  border: 'none',
  borderRadius: RADIUS,
  fontSize: 15,
  fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontFamily: 'inherit',
})

const linkBtn = {
  background: 'none',
  border: 'none',
  color: COBALT,
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
  padding: 0,
  fontFamily: 'inherit',
}

const ErrorBanner = ({ children }) =>
  children ? (
    <div
      style={{
        background: '#FEF2F2',
        border: '1px solid #FECACA',
        color: ERROR,
        borderRadius: RADIUS,
        padding: '10px 12px',
        fontSize: 13,
        marginBottom: 14,
      }}
    >
      {children}
    </div>
  ) : null

// Which phone channels the OTP went out on, phrased for "Code sent to … and <label> ••••1234".
const phoneChannelLabel = ({ sms_sent: sms, whatsapp_sent: whatsapp } = {}) => {
  if (sms && whatsapp) return 'texted + sent on WhatsApp to'
  if (whatsapp) return 'sent on WhatsApp to'
  return 'texted to'
}

const InviteStart = () => {
  const { inviteToken } = useParams()
  const navigate = useNavigate()

  const [stage, setStage] = useState('loading') // loading | email | otp | invalid
  const [invite, setInvite] = useState(null)
  const [email, setEmail] = useState('')
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', ''])
  const [error, setError] = useState('')
  const [sending, setSending] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [resendIn, setResendIn] = useState(0)
  const [maskedEmail, setMaskedEmail] = useState('')
  const [maskedPhone, setMaskedPhone] = useState('')
  const [phoneVia, setPhoneVia] = useState('')

  const otpRefs = [useRef(null), useRef(null), useRef(null), useRef(null), useRef(null), useRef(null)]

  // ---- Resolve invite on mount
  useEffect(() => {
    let cancelled = false
    const run = async () => {
      try {
        const resp = await resolveInvite(inviteToken)
        if (cancelled) return
        const data = resp?.data?.data || resp?.data
        setInvite(data || {})
        // Already submitted, OR already started (single-attempt — a reload
        // mid-interview must NOT drop the candidate back to the OTP /
        // instructions wall where they could retake). INPROGRESS / DROPOUT /
        // attempted all mean "this attempt is spent".
        const st = String(data?.status || '').toUpperCase()
        const isDone =
          st === 'COMPLETED' ||
          st === 'INPROGRESS' ||
          st === 'DROPOUT' ||
          data?.submitted === true ||
          data?.attempted === true
        trackInviteLinkOpened(
          data?.assessmentType,
          isDone ? 'already_attempted' : 'new',
          data?.role,
          data?.company
        )
        if (isDone) {
          setStage('completed')
          return
        }
        setStage('email')
      } catch (e) {
        if (cancelled) return
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          'This invite link is invalid or expired. Please contact the recruiter.'
        trackInviteLinkInvalid(msg)
        setError(msg)
        setStage('invalid')
      }
    }
    if (inviteToken) run()
    else {
      setError('No invite token in the URL.')
      setStage('invalid')
    }
    return () => {
      cancelled = true
    }
  }, [inviteToken])

  // ---- Resend countdown
  useEffect(() => {
    if (resendIn <= 0) return undefined
    const t = setTimeout(() => setResendIn((n) => Math.max(0, n - 1)), 1000)
    return () => clearTimeout(t)
  }, [resendIn])

  const validateEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || '').trim())

  const handleSendCode = async (e) => {
    if (e?.preventDefault) e.preventDefault()
    setError('')
    const normEmail = email.trim().toLowerCase()
    if (!validateEmail(normEmail)) {
      setError('Please enter a valid email address.')
      return
    }
    setSending(true)
    try {
      const resp = await requestOtp(inviteToken, normEmail)
      const data = resp?.data || {}
      setMaskedEmail(data.masked_email || normEmail)
      setMaskedPhone(data.sms_sent || data.whatsapp_sent ? data.masked_phone || '' : '')
      setPhoneVia(phoneChannelLabel(data))
      setResendIn(data.resend_after || 30)
      setStage('otp')
      setOtpDigits(['', '', '', '', '', ''])
      trackInviteOtpRequested(invite?.assessmentType, false, invite?.role, invite?.company)
      setTimeout(() => otpRefs[0].current?.focus(), 50)
    } catch (err) {
      const msg = err?.response?.data?.message || 'Could not send code. Please try again.'
      trackInviteOtpRequestFailed(invite?.assessmentType, false, msg)
      setError(msg)
    } finally {
      setSending(false)
    }
  }

  const handleResend = async () => {
    if (resendIn > 0) return
    setError('')
    setSending(true)
    try {
      const resp = await resendOtp(inviteToken, email.trim().toLowerCase())
      const data = resp?.data || {}
      setMaskedPhone(data.sms_sent || data.whatsapp_sent ? data.masked_phone || '' : '')
      setPhoneVia(phoneChannelLabel(data))
      setResendIn(data.resend_after || 30)
      setOtpDigits(['', '', '', '', '', ''])
      trackInviteOtpRequested(invite?.assessmentType, true, invite?.role, invite?.company)
      setTimeout(() => otpRefs[0].current?.focus(), 50)
    } catch (err) {
      const msg = err?.response?.data?.message || 'Could not resend code. Please try again.'
      trackInviteOtpRequestFailed(invite?.assessmentType, true, msg)
      setError(msg)
      const after = err?.response?.data?.resend_after
      if (after) setResendIn(after)
    } finally {
      setSending(false)
    }
  }

  const setDigit = (idx, value) => {
    const v = String(value || '').replace(/\D/g, '').slice(0, 1)
    setOtpDigits((prev) => {
      const next = [...prev]
      next[idx] = v
      return next
    })
    if (v && idx < 5) otpRefs[idx + 1].current?.focus()
  }

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !otpDigits[idx] && idx > 0) {
      otpRefs[idx - 1].current?.focus()
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      otpRefs[idx - 1].current?.focus()
    } else if (e.key === 'ArrowRight' && idx < 5) {
      otpRefs[idx + 1].current?.focus()
    } else if (e.key === 'Enter') {
      handleVerify()
    }
  }

  const handleOtpPaste = (e) => {
    const text = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6)
    if (text.length === 0) return
    e.preventDefault()
    const next = ['', '', '', '', '', '']
    for (let i = 0; i < text.length; i += 1) next[i] = text[i]
    setOtpDigits(next)
    const focusIdx = Math.min(text.length, 5)
    otpRefs[focusIdx].current?.focus()
  }

  const handleVerify = async (e) => {
    if (e?.preventDefault) e.preventDefault()
    setError('')
    const code = otpDigits.join('')
    if (code.length !== 6) {
      setError('Please enter the 6-digit code.')
      return
    }
    setVerifying(true)
    try {
      const resp = await verifyOtp(inviteToken, email.trim().toLowerCase(), code)
      const data = resp?.data || {}
      if (!data.token || !data.assessment_assigned_id) {
        throw new Error('Verification failed. Please try again.')
      }

      const assignedId = data.assessment_assigned_id
      // assessmentType / setId / proctoring come from the resolve step; verify
      // may also echo the type. Resolve wins, verify is a fallback.
      const assessmentType =
        invite?.assessmentType || data.assessment_type || 'AI_Interview'
      const assessmentSetId = invite?.assessmentSetId || data.assessment_set_id || ''
      const allowProctoring = invite?.allowProctoring
      // Verification is opt-out: keep it on unless the resolve step said false.
      const allowVerification = invite?.allowVerification
      const studentId = invite?.studentId || ''
      const role = data.role || invite?.role || ''
      const company = data.company || invite?.company || ''
      const assessmentName = invite?.assessmentName || data.assessment_name || ''
      const endTime = invite?.endTime || data.end_time || ''

      // ---- Write the generic invite-session keys (used by every type).
      sessionStorage.setItem(INVITE_SESSION_KEYS.scopedJwt, data.token)
      sessionStorage.setItem(INVITE_SESSION_KEYS.assessmentAssignedId, assignedId)
      if (assessmentSetId)
        sessionStorage.setItem(INVITE_SESSION_KEYS.assessmentSetId, assessmentSetId)
      sessionStorage.setItem(INVITE_SESSION_KEYS.assessmentType, assessmentType)
      if (assessmentName)
        sessionStorage.setItem(INVITE_SESSION_KEYS.name, assessmentName)
      sessionStorage.setItem(INVITE_SESSION_KEYS.email, data.email || email)
      if (role) sessionStorage.setItem(INVITE_SESSION_KEYS.role, role)
      if (company) sessionStorage.setItem(INVITE_SESSION_KEYS.company, company)
      sessionStorage.setItem(
        INVITE_SESSION_KEYS.allowProctoring,
        String(allowProctoring === true || allowProctoring === 'true')
      )
      sessionStorage.setItem(
        INVITE_SESSION_KEYS.allowVerification,
        String(allowVerification !== false && allowVerification !== 'false')
      )
      if (studentId) sessionStorage.setItem(INVITE_SESSION_KEYS.studentId, studentId)
      if (endTime) sessionStorage.setItem(INVITE_SESSION_KEYS.endTime, endTime)

      // ---- Also write the legacy AI-interview keys so the existing
      // instruction/interview code (which still reads them, behind a fallback)
      // keeps working regardless of which screen launched it.
      sessionStorage.setItem('ai_interview_scoped_jwt', data.token)
      sessionStorage.setItem('ai_interview_assessment_assigned_id', assignedId)

      // ---- Analytics: the OTP-verified email is the person. identify() here
      // also back-attributes the anonymous invite_link_opened / invite_otp_*
      // events from this session to them. Never let analytics block a verified
      // candidate from reaching their assessment.
      try {
        // No process.env reads in this chunk — `environment` is already
        // registered globally by initPostHog, and reading process.env here is
        // the pattern that has blanked this bundle before.
        identifyPostHogUser(data.email || email.trim().toLowerCase(), {
          email: data.email || email.trim().toLowerCase(),
          studentId: studentId,
          loginMethod: 'otp_invite',
          entityName: company,
          role: role,
        })
        trackInviteOtpVerified(assignedId, assessmentType, role, company)
      } catch (analyticsErr) {
        console.warn('PostHog identify failed on OTP verify:', analyticsErr)
      }

      // ---- Route by assessment type.
      // Single-id route shape mirrors the AI Interview path (one UUID, not
      // two); setId is already in sessionStorage for the runner to read.
      if (assessmentType === 'AI_Interview') {
        navigate(`/ai-interview/${assignedId}`, { replace: true })
      } else {
        navigate(`/assessment/take/${assignedId}`, { replace: true })
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Incorrect code. Please try again.'
      trackInviteOtpVerificationFailed(invite?.assessmentType, msg)
      setError(msg)
    } finally {
      setVerifying(false)
    }
  }

  const backToEmail = () => {
    setOtpDigits(['', '', '', '', '', ''])
    setError('')
    setStage('email')
  }

  // ---- Render
  const contextLine = invite
    ? [invite.role, invite.company].filter(Boolean).join(' · ')
    : ''

  if (stage === 'loading') {
    return (
      <div style={shell}>
        <div style={card}>
          <div style={{ textAlign: 'center', padding: '40px 0', color: TEXT_SECONDARY, fontSize: 14 }}>
            Loading…
          </div>
        </div>
      </div>
    )
  }

  if (stage === 'invalid') {
    return (
      <div style={shell}>
        <div style={card}>
          <h1 style={heading}>Invite link invalid</h1>
          <p style={{ ...subheading, marginTop: 12 }}>{error}</p>
          <p style={{ ...subheading, marginTop: 16 }}>
            Please contact the recruiter to request a new link.
          </p>
        </div>
      </div>
    )
  }

  if (stage === 'completed') {
    return (
      <div style={shell}>
        <div style={card}>
          <h1 style={heading}>Interview already attempted</h1>
          {contextLine && <p style={subheading}>{contextLine}</p>}
          <p style={{ ...subheading, marginTop: 16 }}>
            This interview has already been started and can only be taken once,
            so it can’t be retaken. Whatever you answered has been recorded for
            the recruiting team — there’s nothing more to do here.
          </p>
          <p style={{ ...subheading, marginTop: 16 }}>
            You can now close this window.
          </p>
        </div>
      </div>
    )
  }

  if (stage === 'email') {
    return (
      <div style={shell}>
        <form style={card} onSubmit={handleSendCode}>
          <h1 style={heading}>Verify your email</h1>
          {invite?.assessmentType === 'AI_Interview' && contextLine && (
            <p style={subheading}>Applying for {contextLine}</p>
          )}
          <div style={{ height: 28 }} />
          <ErrorBanner>{error}</ErrorBanner>
          <label htmlFor="ai-iv-email" style={label}>
            Email address
          </label>
          <input
            id="ai-iv-email"
            type="email"
            autoComplete="email"
            autoFocus
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={(e) => (e.target.style.borderColor = COBALT)}
            onBlur={(e) => (e.target.style.borderColor = BORDER)}
            style={inputBase}
          />
          <div style={{ height: 20 }} />
          <button type="submit" disabled={sending} style={buttonPrimary(sending)}>
            {sending ? 'Sending…' : 'Send code'}
          </button>
          <div style={{ marginTop: 22, textAlign: 'center', fontSize: 13, color: TEXT_SECONDARY }}>
            Having trouble? <a href="mailto:mandate@pluginlive.com" style={{ color: COBALT, fontWeight: 600, textDecoration: 'none' }}>Contact the recruiter</a>
          </div>
        </form>
      </div>
    )
  }

  // OTP stage
  return (
    <div style={shell}>
      <form style={card} onSubmit={handleVerify}>
        <h1 style={heading}>Enter your code</h1>
        <p style={subheading}>
          Code sent to <strong style={{ color: TEXT_PRIMARY }}>{maskedEmail}</strong>
          {maskedPhone ? (
            <>
              {' '}and {phoneVia} <strong style={{ color: TEXT_PRIMARY }}>{maskedPhone}</strong>
            </>
          ) : null}
          . Valid for a few minutes.
        </p>
        <div style={{ height: 20 }} />
        <ErrorBanner>{error}</ErrorBanner>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 10,
            marginBottom: 22,
            width: '100%',
          }}
        >
          {otpDigits.map((d, i) => (
            <input
              key={i}
              ref={otpRefs[i]}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              value={d}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => handleOtpKeyDown(i, e)}
              onPaste={handleOtpPaste}
              onFocus={(e) => {
                e.target.select()
                e.target.style.borderColor = COBALT
              }}
              onBlur={(e) => (e.target.style.borderColor = BORDER)}
              style={{
                width: 48,
                minWidth: 0,
                maxWidth: 48,
                height: 56,
                padding: 0,
                textAlign: 'center',
                fontSize: 22,
                fontWeight: 700,
                color: TEXT_PRIMARY,
                border: `1px solid ${BORDER}`,
                borderRadius: RADIUS,
                outline: 'none',
                background: '#fff',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                appearance: 'none',
              }}
            />
          ))}
        </div>
        <button
          type="submit"
          disabled={verifying || otpDigits.join('').length !== 6}
          style={buttonPrimary(verifying || otpDigits.join('').length !== 6)}
        >
          {verifying ? 'Verifying…' : 'Verify'}
        </button>
        <div style={{ marginTop: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button type="button" onClick={backToEmail} style={linkBtn}>
            ← Use a different email
          </button>
          {resendIn > 0 ? (
            <span style={{ fontSize: 13, color: TEXT_SECONDARY }}>Resend in {resendIn}s</span>
          ) : (
            <button type="button" onClick={handleResend} style={linkBtn} disabled={sending}>
              {sending ? 'Sending…' : 'Resend code'}
            </button>
          )}
        </div>
      </form>
    </div>
  )
}

export default InviteStart
